import { List, Map, Record } from 'immutable'
import moment from 'moment'
import momentTimezone from 'moment-timezone'
// @ts-ignore
import { NOTIFICATION_HOURS_DEFAULTS } from 'app/constants/notificationHours'
// @ts-ignore
import { ActiveProTrackLevel } from 'store/records/proTracks'
// @ts-ignore
import { Activity } from 'store/records/activity'

import {
  IActivitiesHistory,
  IActivitiesHistoryItem,
  IActivitiesHistoryPagination,
  IAuthToken,
  IFeaturesNotification,
  IIdentity,
  IIdentityBeforeLoading,
  IIdentityOrganization,
  IIdentityPending,
  INotificationType,
  IOnboardingChecklist,
  IOnboardingLevel,
  IOnboardingLevels,
  IOnboardingProTrackRecord,
  IProgress,
  IProgressAllInstance,
  IProgressCurrentInstance,
  ISprint,
  ISprintGoal,
} from 'store/entities/identity/interface'
import { IActivityRecord } from 'store/entities/activity/interface'

export const AuthTokenRecord = Record<IAuthToken>({
  accessToken: null,
  expiresIn: null,
})

export class AuthToken extends AuthTokenRecord {}

export const IdentityPendingRecord = Record<IIdentityPending>({
  getProgress: false,
  getProfile: false,
  saveProfile: false,
  getEmailQuery: false,
  proTrackSubscription: false,
  getHistory: false,
  getCombinedProgress: false,
})

export class IdentityPending extends IdentityPendingRecord {}

export const IdentityBeforeLoadingRecord = Record<IIdentityBeforeLoading>({
  profile: false,
})

export class IdentityBeforeLoading extends IdentityBeforeLoadingRecord {}

export class ProgressAllInstance extends Record<IProgressAllInstance>({
  commitsCount: 0,
  completedCount: 0,
  streakCurrent: 0,
  streakMax: 0,
  storiesShared: 0,
  storiesReactions: 0,
}) {}

export class ProgressCurrentInstance extends Record<IProgressCurrentInstance>({
  commitsCount: 0,
  completedCount: 0,
  progressDayForLevel: 0,
  proTrackLevelLength: 0,
  proTrackProgress: 0,
  streakCurrentForLevel: 0,
  streakMaxForLevel: 0,
  storiesShared: 0,
  storiesReactions: 0,
}) {}

export class Progress extends Record<IProgress>({
  all: new ProgressAllInstance(),
  current: new ProgressCurrentInstance(),
}) {}

const OnboardingProTrackRecord = Record<IOnboardingProTrackRecord>({
  nextLevel: null,
  previousLevels: List(),
})

export class OnboardingProTrack extends OnboardingProTrackRecord {}

export const OnboardingLevelsRecord = Record<IOnboardingLevels>({
  current: Map(),
  new: List(),
  previous: List(),
  levels: List(),
})

export class OnboardingLevels extends OnboardingLevelsRecord {}

export const OnboardingLevelRecord = Record<IOnboardingLevel>({
  color: null,
  exampleActivities: List(),
  finishText: null,
  goals: null,
  id: null,
  image: null,
  name: null,
  order: null,
  postSurveyLink: null,
  preSurveyLink: null,
  proTrackId: null,
  proTrackName: null,
  welcomeText: null,
  activities: List<IActivityRecord>(),
  finished: false,
})

export class OnboardingLevel extends OnboardingLevelRecord {}

export const IdentityOrganizationRecord = Record<IIdentityOrganization>({
  active: false,
  domain: null,
  id: null,
  logo: null,
  name: null,
  hasIrregularWorkWeek: false,
  demoMode: false,
  teamOnly: false,
  proTrackLevelEndsByActivityCount: false,
  isApiKeyEnabled: false,
  displayName: null,
  emailFrom: null,
  emailProviderApiKey: null,
  sms: false,
  stories: false,
  reactions: false,
  peerInvites: false,
  social: false,
  welcomeEmail: Map(),
  language: null,
  isSprintsActive: false,
  changeNotificationFrequency: false,
  isPreTrackSurveyEnabled: false,
  isPostTrackSurveyEnabled: false,
})

export class IdentityOrganization extends IdentityOrganizationRecord {}

export const ActivitiesHistoryPaginationRecord = Record<IActivitiesHistoryPagination>({
  limit: null,
  count: null,
  page: null,
})

export class ActivitiesHistoryPagination extends ActivitiesHistoryPaginationRecord {}

export const ActivitiesHistoryItemRecord = Record<IActivitiesHistoryItem>({
  activityId: null,
  image: null,
  activityVideoId: null,
  videoThumbnail: null,
  name: null,
  description: null,
  proQuote: null,
  proQuoteAuthor: null,
  day: null,
  completed: null,
  actionType: null,
  userActivityId: null,
  userProTrackLevelId: null,
  hasStory: null,
  question: null,
  story: {
    id: null,
    story: null,
  },
})

export const ActivitiesHistoryRecord = Record<IActivitiesHistory>({
  items: List<IActivitiesHistoryItem>(),
  pending: false,
  pagination: new ActivitiesHistoryPagination(),
})

export class ActivitiesHistory extends ActivitiesHistoryRecord {}

export class ActivitiesHistoryItem extends ActivitiesHistoryItemRecord {}

const NotificationTypeRecord = Record<INotificationType>({
  email: true,
  sms: false,
})

export class NotificationTypes extends NotificationTypeRecord {}

const FeaturesNotificationRecord = Record<IFeaturesNotification>({
  sms: null,
  smsTooltip: null,
  stories: null,
})

export class FeaturesNotification extends FeaturesNotificationRecord {}

const OnboardingChecklistRecord = Record<IOnboardingChecklist>({
  isFirstActionCompleted: false,
  isStoryShared: false,
  isMobileNumberAdded: false,
  areNotificationSettingsUpdated: false,
  isQuizCompleted: false,
})

export class OnboardingChecklist extends OnboardingChecklistRecord {}

export const SprintGoalRecord = Record<ISprintGoal>({
  achievedPoints: 0,
  goal: 0,
  goalAchievedMessage: false,
})

const SprintRecord = Record<ISprint>({
  name: null,
  title: null,
  endDate: new Date(),
  badge: null,
  user: new SprintGoalRecord(),
  organization: new SprintGoalRecord(),
  result: null,
})

export class Sprint extends SprintRecord {}

export const IdentityRecord = Record<IIdentity>(
  {
    id: null,
    role: List(),
    authToken: new AuthToken(),
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    phoneISO: null,
    avatar: null,
    firstLogin: false,
    day: null,
    stage: null,
    roles: List(),
    activeRole: 0,
    pending: new IdentityPending(),
    activitiesHistory: new ActivitiesHistory(),
    teams: List(),
    onboardingChecklist: new OnboardingChecklist(),

    /** Time Preferences */
    commitNotificationTime: NOTIFICATION_HOURS_DEFAULTS.MORNING,
    committedNotificationTime: NOTIFICATION_HOURS_DEFAULTS.EVENING,
    notificationType: new NotificationTypes(),
    timezone: momentTimezone.tz.guess(),
    workWeek: null,
    // if true it means that user saved his workWeek preferences at least once
    isWorkWeekTouched: null,

    /** User protrack activity data */
    actions: Map(),

    /** Selected (active) proTrack (full object) */
    activeLevel: new ActiveProTrackLevel(),
    progress: new Progress(),
    beforeLoading: new IdentityBeforeLoading(),
    proTrackStartDate: null,
    canCompletePreviousActivity: false,
    isCommitNotificationTimeHasCome: false,
    isCommittedNotificationTimeHasCome: false,
    isUserCanSwitchProtrack: false,
    dayoffs: List(),
    organizationId: null,
    organization: new IdentityOrganization(),
    lastActivity: new Activity(),
    isTokenSendToEmail: false,
    isTokenSendToPhone: false,
    onboardingLevels: new OnboardingLevels(),
    isShowFeatureInfo: new FeaturesNotification(),
    isUserHaveOnlyFirstActivity: false,
    previousProTrackLevel: new ActiveProTrackLevel(),
    isLoginFlowFinished: false,
    sprint: new Sprint(),
    badges: null,
    isWorkingDay: false,
    /** Desktop Notifications */
    oneSignalAppId: null,
    oneSignals: List(),
    isUserEverTakeProTrack: false,
  },
  'Identity'
)

export class Identity extends IdentityRecord {
  getName() {
    if (this.firstName) {
      return this.firstName
    }
    if (this.lastName) {
      return this.lastName
    }
    return this.email
  }

  getInitials() {
    const firstName = this.firstName
    const lastName = this.lastName
    const email = this.email
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase()
    } else if (email) {
      // @ts-ignore
      return email.substring(0, 2).toUpperCase()
    } else {
      return '--'
    }
  }

  getTimezone() {
    return this.timezone || moment.tz.guess()
  }
}
